import { useItems, useUpdateItem } from '@api/generics';
import QueryKeys from '@api/index';
import API_ROUTES from '@api/routes';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import { LoadingButton } from '@mui/lab';
import {
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid
} from '@mui/material';
import {
	MAX_CHARS_AUTHORIZATION,
	stringRequiredMaxCharsValidationScheme
} from '@utils/validation';
import { FormikItemsAutocomplete } from 'components/Forms';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import React from 'react';
import { IPermission, IRole } from 'types/authorization';
import { IRowActionDialogProps } from 'types/common';
import { object } from 'yup';

const RoleEditDialog: React.FC<IRowActionDialogProps> = ({
	onComplete,
	row
}) => {
	const { invokeToastableAction } = useToast();
	const { data } = useItems<IPermission>(
		QueryKeys.permissions,
		API_ROUTES.v1.permissions.all
	);
	const editMutation = useUpdateItem(API_ROUTES.v1.roles.update, 'roles');

	const formik = useFormik<IRole>({
		initialValues: row?.row || {},
		validationSchema: object({
			name: stringRequiredMaxCharsValidationScheme(MAX_CHARS_AUTHORIZATION)
		}),
		onSubmit: async (updatedItem: IRole) => {
			await invokeToastableAction(
				() => editMutation.mutateAsync(updatedItem),
				'roles.action.edit.dialog.toast',
				onComplete
			);
		}
	});

	return (
		<Dialog open maxWidth="xs">
			<DialogTitle>{intl.tr('roles.action.edit.dialog.title')}</DialogTitle>
			<DialogContent>
				<Grid container spacing={2} pt={2} pb={2}>
					<Grid item xs={12}>
						<TextFieldValidation
							id="name"
							formik={formik}
							required
							fullWidth
							autoFocus
							label={intl.tr('fields.roles.column.name')}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextFieldValidation
							id="description"
							formik={formik}
							fullWidth
							label={intl.tr('fields.roles.column.description')}
						/>
					</Grid>
					<Grid item xs={12}>
						<FormikItemsAutocomplete
							id="permissions"
							formik={formik}
							data={data?.data || []}
							textLabel={intl.tr('roles.action.edit.dialog.field.permissions')}
						/>
					</Grid>
					<Grid item xs={12}>
						<Alert severity="warning" sx={{ marginTop: 2, marginBottom: 2 }}>
							{intl.tr('roles.action.edit.dialog.warning')}
						</Alert>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={onComplete}
					data-testid="role-edit-dialog-cancel-button"
				>
					{intl.tr('button.cancel')}
				</Button>
				<LoadingButton
					variant="contained"
					color="primary"
					loading={formik.isSubmitting}
					disabled={!formik.isValid}
					onClick={() => formik.handleSubmit()}
					data-testid="role-edit-dialog-approve-button"
				>
					{intl.tr('button.save')}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default RoleEditDialog;
